import { Calendar, DollarSign, UserPlus, TrendingUp, Clock } from "lucide-react";
import { StatCard } from "@/components/schedora/StatCard";
import { AppointmentCard } from "@/components/schedora/AppointmentCard";
import { useOwnerSnapshot } from "@/context/AppContext";
import { useWelcome } from "@/hooks/use-welcome";

const today = new Date().toISOString().slice(0, 10);

export default function OwnerDashboard() {
  const snapshot = useOwnerSnapshot(today);
  const welcome = useWelcome();
  const todayAppts = snapshot?.views.filter((appointment) => appointment.date === today) ?? [];
  const recentActivity = snapshot?.views
    .slice()
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))
    .slice(0, 4) ?? [];

  return (
    <div className="space-y-6">
      {welcome?.role === "owner" && (
        <div className="rounded-xl border bg-card p-5 shadow-soft">
          <p className="text-sm font-semibold text-foreground">Welcome to Schedora, {welcome.name}.</p>
          <p className="mt-1 text-sm text-muted-foreground">Your business workspace is live. Add services, staff, and hours to start taking bookings.</p>
        </div>
      )}
      <div>
        <h1 className="text-2xl font-heading font-bold text-foreground">Dashboard</h1>
        <p className="text-sm text-muted-foreground">Live overview of bookings, revenue, and client activity</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Today's Bookings" value={snapshot?.stats.todayBookings ?? 0} icon={Calendar} trend="Live booking count" trendUp />
        <StatCard title="Revenue Today" value={`$${(snapshot?.stats.revenue ?? 0).toLocaleString()}`} icon={DollarSign} trend="Projected from today's schedule" trendUp />
        <StatCard title="New Clients" value={snapshot?.stats.newClients ?? 0} icon={UserPlus} trend="Clients with first booking" trendUp />
        <StatCard title="Completion Rate" value={`${snapshot?.stats.completionRate ?? 0}%`} icon={TrendingUp} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <h2 className="text-lg font-heading font-semibold mb-3">Today's Schedule</h2>
          <div className="space-y-2">
            {todayAppts.map((appointment) => (
              <AppointmentCard
                key={appointment.id}
                client={appointment.clientName}
                service={appointment.serviceName}
                staff={appointment.staffName}
                time={appointment.timeLabel}
                duration={appointment.duration}
                status={appointment.status}
                price={appointment.servicePrice}
                compact
              />
            ))}
            {todayAppts.length === 0 && (
              <div className="rounded-lg border border-dashed bg-card p-4 text-sm text-muted-foreground">
                No appointments are booked for today yet.
              </div>
            )}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-heading font-semibold mb-3">Recent Activity</h2>
          <div className="space-y-2">
            {recentActivity.map((item) => (
              <div key={item.id} className="flex items-start gap-3 rounded-lg border bg-card p-3">
                <Clock className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                <div>
                  <p className="text-sm">{item.clientName} {item.status === "cancelled" ? "cancelled" : "updated"} {item.serviceName}</p>
                  <p className="text-xs text-muted-foreground">{item.date} at {item.timeLabel}</p>
                </div>
              </div>
            ))}
            {recentActivity.length === 0 && (
              <div className="rounded-lg border border-dashed bg-card p-4 text-sm text-muted-foreground">
                Activity will appear here as bookings change.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
